/**
 * <s-app-install> — "Get the app" button (footer band).
 *
 * - Browsers that offer installing (Chrome on Android/desktop, Edge): the button opens the native install prompt.
 * - Otherwise the button reveals step-by-step instructions: Share → Add to Home Screen on iPhone/iPad,
 *   browser menu → Install app / Add to Home screen elsewhere.
 * - Hidden entirely when the store is already open as the installed app.
 *
 * Markup contract:
 *   button[command="--install"][commandfor=<this id>]  the action
 *   [data-app-steps]                                     instructions, hidden until needed
 * Sets data-mode="native" | "ios" | "manual" on the host so CSS shows the matching instruction text.
 */
class SAppInstall extends HTMLElement {
  #ac;
  #deferred = null;

  connectedCallback() {
    if (window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true) {
      this.hidden = true;
      return;
    }
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.dataset.mode = this.#isIos() ? 'ios' : 'manual';

    window.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault();
      this.#deferred = event;
      this.dataset.mode = 'native';
    }, opts);
    window.addEventListener('appinstalled', () => { this.hidden = true; }, opts);

    this.addEventListener('command', (event) => {
      if (event.command === '--install') this.#install();
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #install() {
    if (this.#deferred) {
      const prompt = this.#deferred;
      this.#deferred = null;
      prompt.prompt();
      try {
        await prompt.userChoice;
      } catch {
        // Closed without a choice.
      }
      this.dataset.mode = this.#isIos() ? 'ios' : 'manual';
      return;
    }
    const steps = this.querySelector('[data-app-steps]');
    if (steps) steps.hidden = !steps.hidden;
  }

  #isIos() {
    const iPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    return /iphone|ipad|ipod/i.test(navigator.userAgent) || iPadOS;
  }
}

if (!customElements.get('s-app-install')) customElements.define('s-app-install', SAppInstall);
